import { CalculatorContext } from '../machines/calculator.machine'
import { EmailPayload, emailPayloadSchema } from '../machines/emailReport.machine'
import { PaymentFrequencies, PaymentFrequency, PaymentFrequencyValues } from '../types/global'
import { toCurrency } from './currency'
import { variablePrecision } from './variablePrecision'

export type EmailPayloadParameters = Omit<CalculatorContext, 'emailRef'>
export function buildEmailPayload(context: EmailPayloadParameters, toEmail = ''): EmailPayload {
  return emailPayloadSchema.parse({
    toEmail,
    emailTemplateModel: buildEmailTemplateModel(context),
  })
}

export const paymentFrequencyToString = (paymentFrequency: PaymentFrequencyValues) =>
  (Object.entries(PaymentFrequency).find(([, v]) => v === paymentFrequency)?.[0] || 'Annually') as PaymentFrequencies

export const annualConsumptionToString = (
  annualConsumption: number,
  annualConsumptionOptions: CalculatorContext['annualConsumptionOptions'],
) => annualConsumptionOptions.find(({ value }) => value === annualConsumption)

export function buildEmailTemplateModel({
  costPerUnit,
  emissionFactor,
  carEmissionFactor,
  gst,
  annualConsumption,
  naturalPowerRatio,
  paymentFrequency,
  currentCost,
  annualConsumptionOptions,
}: EmailPayloadParameters): EmailPayload['emailTemplateModel'] & {
  naturalPowerRatio: number
} {
  const selectedAnnualConsumption = annualConsumptionToString(annualConsumption, annualConsumptionOptions)
  const annualCost = Number(currentCost)
    ? Number(currentCost) * paymentFrequency
    : selectedAnnualConsumption?.data?.averageCost || 0
  const estimatedPremium = costPerUnit * annualConsumption * naturalPowerRatio * gst
  const estimatedTotal = estimatedPremium + annualCost
  const co2Saved = (annualConsumption * naturalPowerRatio * emissionFactor) / 1000
  const carsRemoved = co2Saved / carEmissionFactor
  const frequency = paymentFrequencyToString(paymentFrequency)
  return {
    naturalPowerRatio,
    naturalPowerContribution: `${naturalPowerRatio * 100}%`,
    annualConsumption: `${selectedAnnualConsumption?.label}kWh`,
    annualConsumptionAverage: `${annualConsumption.toLocaleString('en-AU', { maximumFractionDigits: 0 })}kWh`,
    currentCost: toCurrency(currentCost),
    paymentFrequency: frequency,
    annualCost: toCurrency(annualCost),
    estimatedPremium: toCurrency(estimatedPremium),
    estimatedTotal: toCurrency(estimatedTotal),
    co2Saved: variablePrecision(co2Saved),
    carsRemoved: variablePrecision(carsRemoved),
  }
}
